import { Role } from "@/types/user";
import type { UserRoleFilter } from "@/types/user";
import type { CustomRoleScope } from "@/components/pages/users/hooks/useCustomRoles";
import type { AccessGroupScope } from "@/types/access-group";
import { PlatformRole } from "@/utils/accessControl";

export enum UsersTabKey {
  USERS = "USERS",
  GROUPS = "GROUPS",
  ROLES = "ROLES",
}

export type UsersPageRole = "SUPER_ADMIN" | "GUESTERIA_ADMIN" | Role;

export type UsersPageUiConfig = {
  allowedTabs: UsersTabKey[];
  possibleRoles: UserRoleFilter[];
  userListSource: "users" | "guesteria-admins";
  canAddUser: boolean;
  canAddGroup: boolean;
  canAddCustomRole: boolean;
  // Gate for the edit affordances (Edit button → `?edit=true`).
  canOpenUserDetails: boolean;
  // Gate for opening the read-only detail/preview screen via a row click.
  // "View" roles get this without `canOpenUserDetails`.
  canViewUserDetails: boolean;
  canViewGroupDetails: boolean;
  canEditGuesteriaAdmin: boolean;
  canDeleteUser: boolean;
  showTenantColumn: boolean;
  customRoleScope: CustomRoleScope;
  accessGroupScope: AccessGroupScope;
};

const TENANT_ADMIN_TABS: UsersTabKey[] = [
  UsersTabKey.USERS,
  UsersTabKey.GROUPS,
  UsersTabKey.ROLES,
];

const USERS_ONLY_TABS: UsersTabKey[] = [UsersTabKey.USERS];

const TENANT_ADMIN_UI_CONFIG: UsersPageUiConfig = {
  allowedTabs: TENANT_ADMIN_TABS,
  possibleRoles: [
    Role.TENANT_ADMIN,
    Role.HOTEL_ADMIN,
    Role.FRONT_DESK,
    Role.STAFF,
  ],
  userListSource: "users",
  canAddUser: true,
  canAddGroup: true,
  canAddCustomRole: true,
  canOpenUserDetails: true,
  canViewUserDetails: true,
  canViewGroupDetails: true,
  canEditGuesteriaAdmin: false,
  canDeleteUser: true,
  showTenantColumn: false,
  customRoleScope: "tenant",
  accessGroupScope: "tenant",
};

const SUPER_ADMIN_UI_CONFIG: UsersPageUiConfig = {
  allowedTabs: TENANT_ADMIN_TABS,
  possibleRoles: [PlatformRole.GUESTERIA_ADMIN],
  userListSource: "guesteria-admins",
  canAddUser: true,
  canAddGroup: true,
  canAddCustomRole: true,
  canOpenUserDetails: false,
  canViewUserDetails: false,
  canViewGroupDetails: true,
  canEditGuesteriaAdmin: true,
  canDeleteUser: true,
  showTenantColumn: false,
  customRoleScope: "platform",
  accessGroupScope: "admin",
};

const GUESTERIA_ADMIN_UI_CONFIG: UsersPageUiConfig = {
  allowedTabs: USERS_ONLY_TABS,
  possibleRoles: [
    Role.TENANT_ADMIN,
    Role.HOTEL_ADMIN,
    Role.FRONT_DESK,
    Role.STAFF,
  ],
  userListSource: "users",
  canAddUser: false,
  canAddGroup: false,
  canAddCustomRole: false,
  canOpenUserDetails: false,
  canViewUserDetails: false,
  canViewGroupDetails: false,
  canEditGuesteriaAdmin: false,
  canDeleteUser: false,
  showTenantColumn: true,
  customRoleScope: "platform",
  accessGroupScope: "admin",
};

const HOTEL_SCOPED_UI_CONFIG: UsersPageUiConfig = {
  allowedTabs: USERS_ONLY_TABS,
  possibleRoles: [Role.HOTEL_ADMIN, Role.FRONT_DESK, Role.STAFF],
  userListSource: "users",
  canAddUser: false,
  canAddGroup: false,
  canAddCustomRole: false,
  canOpenUserDetails: false,
  canViewUserDetails: false,
  canViewGroupDetails: false,
  canEditGuesteriaAdmin: false,
  canDeleteUser: false,
  showTenantColumn: false,
  customRoleScope: "tenant",
  accessGroupScope: "tenant",
};

const USERS_PAGE_CONFIG_BY_ROLE: Record<UsersPageRole, UsersPageUiConfig> = {
  SUPER_ADMIN: SUPER_ADMIN_UI_CONFIG,
  GUESTERIA_ADMIN: GUESTERIA_ADMIN_UI_CONFIG,
  [Role.TENANT_ADMIN]: TENANT_ADMIN_UI_CONFIG,
  [Role.HOTEL_ADMIN]: HOTEL_SCOPED_UI_CONFIG,
  [Role.FRONT_DESK]: HOTEL_SCOPED_UI_CONFIG,
  [Role.STAFF]: HOTEL_SCOPED_UI_CONFIG,
};

export function resolveUsersPageUiConfig(
  role?: string | null,
): UsersPageUiConfig {
  if (!role) {
    return HOTEL_SCOPED_UI_CONFIG;
  }

  return (
    USERS_PAGE_CONFIG_BY_ROLE[role as UsersPageRole] ?? HOTEL_SCOPED_UI_CONFIG
  );
}
